import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';

import {
    listarMisRutas,
    crearRuta,
    actualizarRuta,
    anularRuta
} from '../../services/Siniestro/RutaGuardadaService';
import { listarMisGrupos } from '../../services/Siniestro/SiniestroGrupoService';
import DibujadorDeRutaGuardada from './DibujadorDeRutaGuardada';

const VALOR_PERSONAL = -1; // valor del Dropdown que indica "ruta personal" (no compartida)

const opcionPersonal = { siniestroGrupoId: VALOR_PERSONAL, nombre: 'Personal (solo tu)' };

const filaVacia = {
    rutaGuardadaId: null,
    nombre: '',
    radioKm: 1,
    siniestroGrupoId: VALOR_PERSONAL,
    origenLabel: '', origenLat: null, origenLng: null,
    destinoLabel: '', destinoLat: null, destinoLng: null,
    polilineaEncoded: null,
    tiposEventoCsv: null
};

/**
 * Dialogo de "Mis Rutas Guardadas" para la pantalla UbicacionSiniestro.
 * Pestañas: lista (Mis Rutas) y formulario (crear/editar).
 *
 * Props:
 *   visible   : boolean
 *   onHide    : () => void          // cerrar el dialogo
 *   onCambios : () => void          // notifica al padre que hubo create/update/anular
 */
const DialogoMisRutas = ({ visible, onHide, onCambios }) => {
    const [vista, setVista] = useState('lista'); // 'lista' | 'form'
    const [misRutas, setMisRutas] = useState([]);
    const [misGrupos, setMisGrupos] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [filtro, setFiltro] = useState('');
    const [editar, setEditar] = useState(filaVacia);
    const toast = useRef(null);
    const datRutaRef = useRef(null); // ultimo cambio del DibujadorDeRutaGuardada

    const cargarTodo = async () => {
        setCargando(true);
        try {
            const [rutas, grupos] = await Promise.all([listarMisRutas(), listarMisGrupos()]);
            setMisRutas(Array.isArray(rutas) ? rutas : []);
            setMisGrupos(Array.isArray(grupos) ? grupos : []);
        } catch (e) {
            console.error('ERROR: cargando mis rutas / grupos', e);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron cargar las rutas o los grupos.'
            });
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        if (visible) {
            setVista('lista');
            cargarTodo();
        }
    }, [visible]);

    const dropdownGrupos = [opcionPersonal, ...misGrupos.map((g) => ({
        siniestroGrupoId: g.siniestroGrupoId,
        nombre: g.nombre
    }))];

    const abrirNueva = () => {
        setEditar({ ...filaVacia });
        datRutaRef.current = null;
        setVista('form');
    };

    const abrirEdicion = (rowData) => {
        setEditar({
            rutaGuardadaId: rowData.rutaGuardadaId,
            nombre: rowData.nombre || '',
            radioKm: rowData.radioKm ?? 1,
            siniestroGrupoId: rowData.siniestroGrupoId ?? VALOR_PERSONAL,
            origenLabel: rowData.origenLabel || '',
            origenLat: rowData.origenLat ?? null,
            origenLng: rowData.origenLng ?? null,
            destinoLabel: rowData.destinoLabel || '',
            destinoLat: rowData.destinoLat ?? null,
            destinoLng: rowData.destinoLng ?? null,
            polilineaEncoded: rowData.polilineaEncoded ?? null,
            tiposEventoCsv: rowData.tiposEventoCsv ?? null
        });
        datRutaRef.current = null;
        setVista('form');
    };

    const cancelarForm = () => {
        setVista('lista');
        setEditar(filaVacia);
        datRutaRef.current = null;
    };

    const validar = (dat) => {
        if (!editar.nombre || !editar.nombre.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Atencion', detail: 'Indica un nombre para la ruta.' });
            return false;
        }
        if (!dat || dat.origenLat == null || dat.origenLng == null) {
            toast.current?.show({ severity: 'warn', summary: 'Atencion', detail: 'Selecciona el origen.' });
            return false;
        }
        if (dat.destinoLat == null || dat.destinoLng == null) {
            toast.current?.show({ severity: 'warn', summary: 'Atencion', detail: 'Selecciona el destino.' });
            return false;
        }
        if (!editar.radioKm || editar.radioKm <= 0) {
            toast.current?.show({ severity: 'warn', summary: 'Atencion', detail: 'El radio debe ser mayor a 0.' });
            return false;
        }
        return true;
    };

    const guardar = async () => {
        const dat = datRutaRef.current || {
            origenLabel: editar.origenLabel, origenLat: editar.origenLat, origenLng: editar.origenLng,
            destinoLabel: editar.destinoLabel, destinoLat: editar.destinoLat, destinoLng: editar.destinoLng,
            polilineaEncoded: editar.polilineaEncoded
        };
        if (!validar(dat)) return;

        const grupoIdLimpio = editar.siniestroGrupoId === VALOR_PERSONAL ? null : editar.siniestroGrupoId;

        const payload = {
            nombre: editar.nombre.trim(),
            radioKm: editar.radioKm,
            tiposEventoCsv: editar.tiposEventoCsv || null,
            origenLabel: dat.origenLabel || null,
            origenLat: dat.origenLat,
            origenLng: dat.origenLng,
            destinoLabel: dat.destinoLabel || null,
            destinoLat: dat.destinoLat,
            destinoLng: dat.destinoLng,
            polilineaEncoded: dat.polilineaEncoded || null,
            siniestroGrupoId: grupoIdLimpio
        };

        try {
            setGuardando(true);
            if (editar.rutaGuardadaId) {
                await actualizarRuta({ rutaGuardadaId: editar.rutaGuardadaId, ...payload });
                toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Ruta actualizada.' });
            } else {
                await crearRuta(payload);
                toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Ruta creada.' });
            }
            await cargarTodo();
            if (onCambios) onCambios();
            setVista('lista');
        } catch (e) {
            console.error('ERROR: guardando ruta', e);
            const msg = e?.response?.data || 'No se pudo guardar la ruta.';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: typeof msg === 'string' ? msg : 'Error al guardar.' });
        } finally {
            setGuardando(false);
        }
    };

    const confirmarAnular = (rowData) => {
        confirmDialog({
            message: `¿Eliminar la ruta "${rowData.nombre}"?`,
            header: 'Confirmar',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await anularRuta(rowData.rutaGuardadaId);
                    toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Ruta eliminada.' });
                    await cargarTodo();
                    if (onCambios) onCambios();
                } catch (e) {
                    console.error('ERROR: anulando ruta', e);
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la ruta.' });
                }
            }
        });
    };

    const grupoBody = (row) => {
        if (row.siniestroGrupoId == null) return <span className="text-500">Personal</span>;
        const g = misGrupos.find((x) => x.siniestroGrupoId === row.siniestroGrupoId);
        return <span>{g?.nombre || 'Grupo ' + row.siniestroGrupoId}</span>;
    };

    const accionesBody = (row) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text p-button-sm"
                tooltip="Editar"
                onClick={() => abrirEdicion(row)}
            />
            <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                tooltip="Eliminar"
                onClick={() => confirmarAnular(row)}
            />
        </div>
    );

    const headerTabla = (
        <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-2">
            <Button label="Nueva ruta" icon="pi pi-plus" onClick={abrirNueva} className="p-button-sm" />
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar..." className="p-inputtext-sm" />
            </span>
        </div>
    );

    const rutasFiltradas = filtro
        ? misRutas.filter((r) => (r.nombre || '').toLowerCase().includes(filtro.toLowerCase()))
        : misRutas;

    return (
        <Dialog
            header={vista === 'lista' ? 'Mis Rutas Guardadas' : (editar.rutaGuardadaId ? 'Editar ruta' : 'Nueva ruta')}
            visible={visible}
            onHide={onHide}
            style={{ width: '90vw', maxWidth: '1100px' }}
            modal
        >
            <Toast ref={toast} />
            <ConfirmDialog />

            {vista === 'lista' && (
                <>
                    {cargando ? (
                        <div className="flex justify-content-center p-5">
                            <ProgressSpinner style={{ width: '40px', height: '40px' }} animationDuration="1s" />
                        </div>
                    ) : (
                        <DataTable
                            value={rutasFiltradas}
                            header={headerTabla}
                            size="small"
                            paginator
                            rows={8}
                            emptyMessage="No tienes rutas guardadas."
                            dataKey="rutaGuardadaId"
                        >
                            <Column field="nombre" header="Nombre" sortable />
                            <Column field="origenLabel" header="Origen" />
                            <Column field="destinoLabel" header="Destino" />
                            <Column field="radioKm" header="Radio (km)" sortable style={{ width: '7rem' }} />
                            <Column header="Compartida con" body={grupoBody} style={{ width: '12rem' }} />
                            <Column body={accionesBody} style={{ width: '7rem' }} />
                        </DataTable>
                    )}
                </>
            )}

            {vista === 'form' && (
                <div className="formgrid grid p-fluid">
                    <div className="field col-12 md:col-5">
                        <label className="font-semibold">Nombre:</label>
                        <InputText
                            value={editar.nombre}
                            onChange={(e) => setEditar({ ...editar, nombre: e.target.value })}
                            placeholder="Ej. Ruta diaria al sur"
                        />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label className="font-semibold">Radio (km):</label>
                        <InputNumber
                            value={editar.radioKm}
                            onValueChange={(e) => setEditar({ ...editar, radioKm: e.value })}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            min={0.1}
                            max={50}
                            step={0.5}
                        />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label className="font-semibold">Compartir con grupo:</label>
                        <Dropdown
                            value={editar.siniestroGrupoId}
                            options={dropdownGrupos}
                            optionLabel="nombre"
                            optionValue="siniestroGrupoId"
                            onChange={(e) => setEditar({ ...editar, siniestroGrupoId: e.value })}
                            placeholder="Personal"
                        />
                    </div>

                    <div className="col-12">
                        <DibujadorDeRutaGuardada
                            valorInicial={editar.rutaGuardadaId ? editar : null}
                            onChange={(d) => { datRutaRef.current = d; }}
                        />
                    </div>

                    <div className="col-12 flex justify-content-end gap-2 mt-3">
                        <Button label="Cancelar" className="p-button-text" onClick={cancelarForm} disabled={guardando} />
                        <Button
                            label={editar.rutaGuardadaId ? 'Guardar cambios' : 'Crear ruta'}
                            icon="pi pi-save"
                            onClick={guardar}
                            loading={guardando}
                        />
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default DialogoMisRutas;
